import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths, addDays } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import type { CalendarEvent, FamilyCustodyEvent, FamilyCustodyRequest, FamilyCustodyShare } from '@/types';
import RecurrenceBuilder, { DEFAULT_RECURRENCE, type RecurrenceData } from '@/components/RecurrenceBuilder';
import SectionNudge from '@/components/SectionNudge';
import FamilyJourneyCard from '@/components/family/FamilyJourneyCard';
import FamilyAdminTab from '@/components/family/FamilyAdminTab';
import FamilyCareReviewSection from '@/components/family/FamilyCareReviewSection';
import FamilyChildcareSection from '@/components/family/FamilyChildcareSection';
import FamilyCustodySection from '@/components/family/FamilyCustodySection';
import SectionShortcutsLauncher from '@/components/SectionShortcutsLauncher';
import TabbedPageShell from '@/components/TabbedPageShell';
import SectionGuideRail from '@/components/playbooks/SectionGuideRail';
import { buildFamilyCustodyScopeFilter, mapFamilyCustodyShareRow } from '@/lib/family-custody';
import { getHouseholdSharePeerName } from '@/lib/household-share';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppSectionHeader,
  AppText,
} from '../../ui-foundation';
import {
  AppDialog as Dialog,
  AppDialogContent as DialogContent,
  AppDialogDescription as DialogDescription,
  AppDialogHeader as DialogHeader,
  AppDialogTitle as DialogTitle,
  AppSelect as Select,
  AppSelectContent as SelectContent,
  AppSelectItem as SelectItem,
  AppSelectTrigger as SelectTrigger,
  AppSelectValue as SelectValue,
  AppPanelButton,
} from '../../ui-foundation/adapters/web';
import {
  buildHouseholdSharingSummary,
  buildSchoolCalendarIdentitySummary,
  groupUpcomingSchoolCalendarEventsBySchool,
  isSchoolWebsiteImport,
} from '../../ui-foundation/features/family';

const EVENT_TYPES = ['appointment', 'school', 'bill', 'birthday', 'other'] as const;

const typeColors: Record<string, string> = {
  appointment: 'bg-teal-light text-teal',
  school: 'bg-plum-light text-primary',
  bill: 'bg-mustard-light text-accent',
  birthday: 'bg-success-light text-success',
  other: 'bg-muted text-muted-foreground',
};

interface EventWithRecurrence extends CalendarEvent {
  recurrence_type?: string;
  recurrence_interval?: number | null;
  recurrence_unit?: string | null;
  recurrence_day_of_week?: number[] | null;
  recurrence_week_of_month?: string | null;
  recurrence_month_day?: number | null;
}

type FamilySchoolCalendarImport = {
  id: string;
  postcode: string;
  authority_name: string;
  source_url: string;
  source_label: string;
  imported_count: number;
  inset_coverage: 'exact' | 'school-specific' | 'unavailable';
  notes: string | null;
  last_imported_at: string;
  selected_school_names: string[];
  lookup_area_label: string | null;
};

type FamilySchoolCalendarEvent = {
  id: string;
  title: string;
  date: string;
  event_kind: string;
  notes: string | null;
  source_label: string;
};

type FamilySchoolLookupCandidate = {
  urn: string;
  name: string;
  postcode: string | null;
  town: string | null;
};

type UpcomingCalendarEntry =
  | { source: 'manual'; event: EventWithRecurrence; sortDate: Date }
  | { source: 'school-import'; event: FamilySchoolCalendarEvent; sortDate: Date };

type FamilyLifeSectionKey = 'profile' | 'school-calendar' | 'events' | 'childcare';

const FAMILY_LIFE_SECTION_EMOJI: Record<FamilyLifeSectionKey, string> = {
  profile: '👨‍👩‍👧‍👦',
  'school-calendar': '🏫',
  events: '🗓️',
  childcare: '🧸',
};

function FamilyLifeSection({
  emoji,
  title,
  subtitle: _subtitle,
  open,
  onToggle,
  children,
}: {
  emoji: string;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <SectionAccordionCard emoji={emoji} title={title} subtitle={_subtitle} open={open} onToggle={onToggle}>
      <div className="space-y-4">{children}</div>
    </SectionAccordionCard>
  );
}

export default function FamilyPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, addEvent, deleteEvent, user } = useApp();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [eventOpen, setEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventWithRecurrence | null>(null);
  const [custodyShare, setCustodyShare] = useState<FamilyCustodyShare | null>(null);
  const [custodyEvents, setCustodyEvents] = useState<FamilyCustodyEvent[]>([]);
  const [custodyRequests, setCustodyRequests] = useState<FamilyCustodyRequest[]>([]);
  const [schoolCalendarImport, setSchoolCalendarImport] = useState<FamilySchoolCalendarImport | null>(null);
  const [schoolCalendarEvents, setSchoolCalendarEvents] = useState<FamilySchoolCalendarEvent[]>([]);
  const [schoolCalendarPostcode, setSchoolCalendarPostcode] = useState(data.profile.postcode || '');
  const [schoolLookupAreaLabel, setSchoolLookupAreaLabel] = useState<string | null>(null);
  const [schoolLookupCandidates, setSchoolLookupCandidates] = useState<FamilySchoolLookupCandidate[]>([]);
  const [selectedSchoolNames, setSelectedSchoolNames] = useState<string[]>([]);
  const [lookingUpSchools, setLookingUpSchools] = useState(false);
  const [importingSchoolCalendar, setImportingSchoolCalendar] = useState(false);
  const [removingSchoolCalendar, setRemovingSchoolCalendar] = useState(false);
  const [expandedSchoolNames, setExpandedSchoolNames] = useState<string[]>([]);
  const [openSections, setOpenSections] = useState<Record<FamilyLifeSectionKey, boolean>>({
    profile: false,
    'school-calendar': false,
    events: false,
    childcare: false,
  });

  const [title, setTitle] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [time, setTime] = useState('09:00');
  const [type, setType] = useState<CalendarEvent['type']>('other');
  const [recurrence, setRecurrence] = useState<RecurrenceData>(DEFAULT_RECURRENCE);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1;

  const today = new Date();
  const householdPartnerName = useMemo(
    () => getHouseholdSharePeerName(data.householdShare, user?.id || null) || data.profile.partnerName.trim() || '',
    [data.householdShare, data.profile.partnerName, user?.id],
  );
  const householdSharingSummary = useMemo(
    () =>
      buildHouseholdSharingSummary({
        share: data.householdShare,
        currentUserId: user?.id || null,
        partnerName: data.profile.partnerName,
      }),
    [data.householdShare, data.profile.partnerName, user?.id],
  );
  const upcoming = data.events
    .filter((event) => {
      const eventDate = new Date(event.date);
      return eventDate >= today && eventDate <= addDays(today, 14);
    })
    .sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime());

  const toggleSection = useCallback((section: FamilyLifeSectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }, []);

  const openFamilyAdminShortcut = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'calendar');
    nextParams.set('compose', 'family-admin');
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const clearFamilyShortcut = useCallback(() => {
    if (!searchParams.get('compose')) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('compose');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!schoolCalendarPostcode && data.profile.postcode) {
      setSchoolCalendarPostcode(data.profile.postcode);
    }
  }, [data.profile.postcode, schoolCalendarPostcode]);

  const loadFamilyCalendarData = useCallback(async () => {
    if (!user) {
      setCustodyShare(null);
      setCustodyEvents([]);
      setCustodyRequests([]);
      setSchoolCalendarImport(null);
      setSchoolCalendarEvents([]);
      return;
    }

    const { data: shareRow } = await supabase
      .from('family_custody_shares' as any)
      .select('*')
      .or(`owner_user_id.eq.${user.id},member_user_id.eq.${user.id}`)
      .maybeSingle();

    const nextCustodyShare = mapFamilyCustodyShareRow(shareRow);
    const custodyScope = buildFamilyCustodyScopeFilter(user.id, nextCustodyShare?.id || null);

    const [{ data: custodyEventRows }, { data: custodyRequestRows }, { data: schoolImportRow }, { data: schoolCalendarRows }] = await Promise.all([
      supabase
        .from('family_custody_events' as any)
        .select('*')
        .or(custodyScope)
        .order('handover_date', { ascending: true })
        .order('handover_time', { ascending: true, nullsFirst: false }),
      supabase
        .from('family_custody_requests' as any)
        .select('*')
        .or(custodyScope)
        .order('requested_for_date', { ascending: true }),
      supabase
        .from('family_school_calendar_imports' as any)
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle(),
      supabase
        .from('family_school_calendar_events' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: true }),
    ]);

    setCustodyShare(nextCustodyShare);
    setCustodyEvents((((custodyEventRows || []) as any[]).map((row) => ({
      id: row.id,
      userId: row.user_id,
      childId: row.child_id,
      shareId: row.share_id || null,
      childName: row.child_name || '',
      handoverDate: row.handover_date,
      handoverTime: row.handover_time,
      handoverKind: row.handover_kind,
      status: row.status,
      location: row.location,
      notes: row.notes,
    }))));

    setCustodyRequests((((custodyRequestRows || []) as any[]).map((row) => ({
      id: row.id,
      userId: row.user_id,
      childId: row.child_id,
      shareId: row.share_id || null,
      childName: row.child_name || '',
      custodyEventId: row.custody_event_id,
      requestedForDate: row.requested_for_date,
      requestType: row.request_type,
      title: row.title,
      details: row.details,
      status: row.status,
      createdAt: row.created_at,
    }))));

    setSchoolCalendarImport((schoolImportRow as FamilySchoolCalendarImport | null) || null);
    setSelectedSchoolNames(
      Array.isArray((schoolImportRow as any)?.selected_school_names)
        ? (schoolImportRow as any).selected_school_names.filter(Boolean)
        : [],
    );
    setSchoolLookupAreaLabel((schoolImportRow as any)?.lookup_area_label || null);
    setSchoolCalendarEvents((((schoolCalendarRows || []) as any[]).map((row) => ({
      id: row.id,
      title: row.title,
      date: row.date,
      event_kind: row.event_kind,
      notes: row.notes,
      source_label: row.source_label || 'School calendar import',
    })) as FamilySchoolCalendarEvent[]));
  }, [user]);

  useEffect(() => {
    void loadFamilyCalendarData();
    const handleRefresh = () => {
      void loadFamilyCalendarData();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [loadFamilyCalendarData]);

  const handleLookupSchools = async () => {
    const postcode = schoolCalendarPostcode.trim() || data.profile.postcode.trim();
    setLookingUpSchools(true);
    const { data: result, error } = await supabase.functions.invoke('family-school-calendar-import', {
      body: { postcode, mode: 'lookup' },
    });
    setLookingUpSchools(false);

    if (error) {
      toast.error(error.message || 'Could not look up nearby schools right now.');
      return;
    }

    if ((result as any)?.error) {
      toast.error(String((result as any).error));
      return;
    }

    const nextSchools = Array.isArray((result as any)?.schools)
      ? ((result as any).schools as FamilySchoolLookupCandidate[])
      : [];
    const nextAreaLabel = typeof (result as any)?.areaLabel === 'string' ? (result as any).areaLabel : null;
    const resolvedPostcode = typeof (result as any)?.postcode === 'string'
      ? String((result as any).postcode).trim()
      : postcode;

    if (resolvedPostcode) {
      setSchoolCalendarPostcode(resolvedPostcode);
    }
    setSchoolLookupCandidates(nextSchools);
    setSchoolLookupAreaLabel(nextAreaLabel);
    setSelectedSchoolNames((current) =>
      current.filter((schoolName) => nextSchools.some((item) => item.name === schoolName))
    );

    if (nextSchools.length) {
      toast.success(`Found ${nextSchools.length} nearby school${nextSchools.length === 1 ? '' : 's'}. Confirm the right one${nextSchools.length === 1 ? '' : 's'} before importing.`);
    } else {
      toast.message('No nearby school names came back from the official schools dataset yet.');
    }
  };

  const toggleSelectedSchool = (name: string) => {
    setSelectedSchoolNames((current) =>
      current.includes(name) ? current.filter((item) => item !== name) : [...current, name]
    );
  };

  const handleImportSchoolCalendar = async () => {
    const postcode = schoolCalendarPostcode.trim() || data.profile.postcode.trim();
    if (schoolLookupCandidates.length > 0 && selectedSchoolNames.length === 0) {
      toast.error('Pick the school or schools that look right before importing the dates.');
      return;
    }

    setImportingSchoolCalendar(true);
    const { data: result, error } = await supabase.functions.invoke('family-school-calendar-import', {
      body: { postcode, selectedSchoolNames },
    });
    setImportingSchoolCalendar(false);

    if (error) {
      toast.error(error.message || 'Could not import school dates right now.');
      return;
    }

    if ((result as any)?.error) {
      toast.error(String((result as any).error));
      return;
    }

    const resolvedPostcode = typeof (result as any)?.postcode === 'string'
      ? String((result as any).postcode).trim()
      : postcode;
    if (resolvedPostcode) {
      setSchoolCalendarPostcode(resolvedPostcode);
    }
    await loadFamilyCalendarData();
    toast.success(
      selectedSchoolNames.length
        ? `Imported ${(result as any)?.importedCount || 0} school dates for ${selectedSchoolNames.join(', ')}.`
        : `Imported ${(result as any)?.importedCount || 0} school dates from ${(result as any)?.authorityName || 'the matched source'}.`
    );
    window.dispatchEvent(new CustomEvent('adulting:data-changed'));
  };

  const handleRemoveSchoolCalendar = async () => {
    if (!user || !schoolCalendarImport) return;
    setRemovingSchoolCalendar(true);

    const { error } = await supabase
      .from('family_school_calendar_imports' as any)
      .delete()
      .eq('user_id', user.id);

    setRemovingSchoolCalendar(false);

    if (error) {
      toast.error('Could not remove the imported school dates right now.');
      return;
    }

    await loadFamilyCalendarData();
    toast.success('Imported school dates removed.');
    window.dispatchEvent(new CustomEvent('adulting:data-changed'));
  };

  const resetForm = () => {
    setTitle('');
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setTime('09:00');
    setType('other');
    setRecurrence(DEFAULT_RECURRENCE);
    setEditingEvent(null);
  };

  const openAdd = () => {
    resetForm();
    setEventOpen(true);
  };

  const openEdit = (event: EventWithRecurrence) => {
    setEditingEvent(event);
    setTitle(event.title);
    setDate(event.date);
    setTime(event.time);
    setType(event.type);
    if (event.recurrence_type === 'custom') {
      setRecurrence({
        recurrence_type: 'custom',
        recurrence_interval: event.recurrence_interval,
        recurrence_unit: event.recurrence_unit,
        recurrence_day_of_week: event.recurrence_day_of_week,
        recurrence_week_of_month: event.recurrence_week_of_month,
        recurrence_month_day: event.recurrence_month_day,
      });
    } else {
      setRecurrence(DEFAULT_RECURRENCE);
    }
    setEventOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!title || !user) return;

    if (editingEvent) {
      const payload = {
        title,
        date,
        time,
        type,
        recurrence_type: recurrence.recurrence_type,
        recurrence_interval: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_interval : null,
        recurrence_unit: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_unit : null,
        recurrence_day_of_week: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_day_of_week : null,
        recurrence_week_of_month: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_week_of_month : null,
        recurrence_month_day: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_month_day : null,
      } as any;
      await supabase.from('events').update(payload).eq('id', editingEvent.id);
      window.location.reload();
    } else {
      const newId = crypto.randomUUID();
      addEvent({ id: newId, title, date, time, type });
      if (recurrence.recurrence_type === 'custom') {
        setTimeout(async () => {
          const { data: events } = await supabase
            .from('events')
            .select('id')
            .eq('user_id', user.id)
            .eq('title', title)
            .eq('date', date)
            .order('created_at', { ascending: false })
            .limit(1);

          if (events?.[0]) {
            await supabase.from('events').update({
              recurrence_type: recurrence.recurrence_type,
              recurrence_interval: recurrence.recurrence_interval,
              recurrence_unit: recurrence.recurrence_unit,
              recurrence_day_of_week: recurrence.recurrence_day_of_week,
              recurrence_week_of_month: recurrence.recurrence_week_of_month,
              recurrence_month_day: recurrence.recurrence_month_day,
            } as any).eq('id', events[0].id);
          }
        }, 500);
      }
    }

    resetForm();
    setEventOpen(false);
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;
    deleteEvent(editingEvent.id);
    resetForm();
    setEventOpen(false);
  };

  const eventsOnDay = (day: Date) => data.events.filter((event) => isSameDay(new Date(event.date), day));
  const importedSchoolEventsOnDay = (day: Date) => schoolCalendarEvents.filter((event) => isSameDay(new Date(event.date), day));
  const custodyEventsOnDay = (day: Date) => custodyEvents.filter((event) => isSameDay(new Date(event.handoverDate), day));

  const upcomingCalendarItems = useMemo<UpcomingCalendarEntry[]>(() => {
    const manualEntries = upcoming.map((event) => ({
      source: 'manual' as const,
      event,
      sortDate: new Date(event.date),
    }));

    const schoolEntries = schoolCalendarEvents
      .filter((event) => {
        const eventDate = new Date(event.date);
        return eventDate >= today && eventDate <= addDays(today, 14);
      })
      .map((event) => ({
        source: 'school-import' as const,
        event,
        sortDate: new Date(event.date),
      }));

    return [...manualEntries, ...schoolEntries]
      .sort((left, right) => left.sortDate.getTime() - right.sortDate.getTime());
  }, [schoolCalendarEvents, today, upcoming]);

  const schoolCalendarIdentity = useMemo(() => buildSchoolCalendarIdentitySummary({
    children: data.profile.children,
    selectedSchoolNames: schoolCalendarImport?.selected_school_names || [],
  }), [data.profile.children, schoolCalendarImport?.selected_school_names]);
  const schoolCalendarIsSchoolSpecificImport = useMemo(
    () => isSchoolWebsiteImport({
      authority_name: schoolCalendarImport?.authority_name,
      source_label: schoolCalendarImport?.source_label,
    }),
    [schoolCalendarImport?.authority_name, schoolCalendarImport?.source_label],
  );
  const schoolCalendarGroupsWithUpcomingDates = useMemo(
    () => groupUpcomingSchoolCalendarEventsBySchool({
      events: schoolCalendarEvents,
      children: data.profile.children,
      selectedSchoolNames: schoolCalendarImport?.selected_school_names || [],
      monthsAhead: 3,
    }),
    [data.profile.children, schoolCalendarEvents, schoolCalendarImport?.selected_school_names],
  );
  const schoolCalendarHasAnyUpcomingSchoolDates = schoolCalendarGroupsWithUpcomingDates.some((group) => group.events.length > 0);
  const toggleExpandedSchool = useCallback((schoolName: string) => {
    setExpandedSchoolNames((current) => (
      current.includes(schoolName)
        ? current.filter((value) => value !== schoolName)
        : [...current, schoolName]
    ));
  }, []);

  const calendarTab = (
    <div className="space-y-4">
      <FamilyLifeSection
        emoji={FAMILY_LIFE_SECTION_EMOJI.profile}
        title="Family profile"
        subtitle="Partner, children, and any linked shared household from Profile."
        open={openSections.profile}
        onToggle={() => toggleSection('profile')}
      >
        <AppText fontSize="$xs" color="$colorMuted">
          Open Profile to change the household link, partner details, or the school saved on a child.
        </AppText>
        {(data.householdShare || householdPartnerName) ? (
          <div className="rounded-2xl border border-border bg-background px-4 py-3">
            <AppText fontSize="$sm" fontWeight="700">Shared household</AppText>
            <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
              {householdSharingSummary.headline}
            </AppText>
            {householdSharingSummary.ownershipLabel ? (
              <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                {householdSharingSummary.ownershipLabel}
              </AppText>
            ) : null}
            <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
              {householdSharingSummary.familySummary}
            </AppText>
          </div>
        ) : null}
        {householdPartnerName && !data.householdShare ? (
          <div className="rounded-2xl border border-border bg-background px-4 py-3">
            <AppText fontSize="$sm" fontWeight="700">Partner / other adult</AppText>
            <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
              {householdPartnerName}
            </AppText>
          </div>
        ) : null}
        {data.profile.children.length ? (
          <div className="space-y-2">
            {data.profile.children.map((child) => (
              <div key={child.id} className="rounded-2xl border border-border bg-background px-4 py-3">
                <AppText fontSize="$sm" fontWeight="700">{child.name || 'Child'}</AppText>
                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                  {`Age ${child.age || 0}`}
                  {child.schoolName ? ` · ${child.schoolName}` : ''}
                </AppText>
              </div>
            ))}
          </div>
        ) : (
          <AppEmptyState
            title="No family details yet"
            body="Add your partner, children, or household link in Profile and they will show here."
          />
        )}
        <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
          Open Profile
        </AppButton>
      </FamilyLifeSection>

      <FamilyLifeSection
        emoji={FAMILY_LIFE_SECTION_EMOJI['school-calendar']}
        title="School calendar"
        subtitle={schoolCalendarImport && schoolCalendarHasAnyUpcomingSchoolDates
          ? 'Saved schools and the next three months of dates.'
          : 'Schools saved on children and the dates Adulting can bring through.'}
        open={openSections['school-calendar']}
        onToggle={() => toggleSection('school-calendar')}
      >
        <AppText fontSize="$xs" color="$colorMuted">
          Adulting uses the schools identified for each child in your profile to bring the term dates through, and sync them with your selected calendar. Go to profile to change the calendar or the school allocated to your child.
        </AppText>
        {schoolCalendarImport ? (
          <>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-plum-light px-3 py-1 text-[11px] font-medium text-primary">
                {schoolCalendarImport.imported_count} dates imported
              </span>
              <span className="rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium text-muted-foreground">
                Last refreshed {format(new Date(schoolCalendarImport.last_imported_at), 'd MMM yyyy')}
              </span>
            </div>
            {schoolCalendarIdentity.groups.length ? (
              <div className="space-y-2">
                {schoolCalendarGroupsWithUpcomingDates.map((group) => {
                  const expanded = expandedSchoolNames.includes(group.schoolName);
                  return (
                    <div key={group.schoolName} className="rounded-2xl border border-border bg-card">
                      <AppPanelButton
                        onClick={() => toggleExpandedSchool(group.schoolName)}
                        className="rounded-2xl border-0 bg-transparent px-4 py-3 hover:bg-muted/30"
                      >
                        <div className="flex w-full items-start justify-between gap-3">
                          <div>
                            <AppText fontSize="$sm" fontWeight="700">{group.schoolName}</AppText>
                            <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                              {group.childNames.length
                                ? `For ${group.childNames.join(', ')}`
                                : 'School is confirmed in the import, but no child is currently linked to it in My Settings.'}
                            </AppText>
                          </div>
                          <AppText fontSize="$xs" color="$colorMuted">
                            {expanded ? 'Hide dates' : 'Show dates'}
                          </AppText>
                        </div>
                      </AppPanelButton>
                      {expanded ? (
                        <div className="space-y-2 border-t border-border px-4 py-3">
                          {schoolCalendarIsSchoolSpecificImport && group.events.length ? (
                            group.events.map((event) => (
                              <div key={event.id} className="rounded-2xl border border-border bg-background px-4 py-3">
                                <AppText fontSize="$sm" fontWeight="700">{event.title}</AppText>
                                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                                  {format(new Date(event.date), 'EEE d MMM')}
                                  {event.notes ? ` · ${event.notes}` : ''}
                                </AppText>
                              </div>
                            ))
                          ) : schoolCalendarIsSchoolSpecificImport ? (
                            <AppText fontSize="$xs" color="$colorMuted">
                              No dates from this school fall in the next three months yet.
                            </AppText>
                          ) : (
                            <AppText fontSize="$xs" color="$colorMuted">
                              These dates are still on the older council-wide import. Open Profile to re-import this school so its own published dates show here.
                            </AppText>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <AppText fontSize="$xs" color="$colorMuted">
                Adulting has imported school dates, but there is not a saved child-school link to show yet.
              </AppText>
            )}
            {schoolCalendarIdentity.importNeedsRefresh ? (
              <AppText fontSize="$xs" color="$colorMuted">
                Schools saved on your children have changed. Open Profile to refresh the school-date import so these cards stay linked to the right people.
              </AppText>
            ) : null}
          </>
        ) : (
          <div className="space-y-3">
            <AppText fontSize="$xs" color="$colorMuted">
              School setup and school-date import now live in Profile. Once they are imported there, each school card here can drop down to show the next few months of dates for that school.
            </AppText>
            <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
              Open Profile
            </AppButton>
          </div>
        )}
      </FamilyLifeSection>

      <FamilyLifeSection
        emoji={FAMILY_LIFE_SECTION_EMOJI.events}
        title="School events & family calendar"
        subtitle="Month view and the next visible family dates."
        open={openSections.events}
        onToggle={() => toggleSection('events')}
      >
        <AppCard>
          <AppSectionHeader
            title={format(currentMonth, 'MMMM yyyy')}
            subtitle="Family events, imported school dates, and custody markers in one month view."
            action={(
              <AppButtonRow>
                <AppButton tone="ghost" size="sm" onPress={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                  <ChevronLeft size={16} />
                </AppButton>
                <AppButton tone="ghost" size="sm" onPress={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                  <ChevronRight size={16} />
                </AppButton>
              </AppButtonRow>
            )}
          />
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground mb-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => <span key={day}>{day}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: startDay }).map((_, index) => <div key={`empty-${index}`} />)}
            {days.map((day) => {
              const hasEvents = eventsOnDay(day).length > 0 || importedSchoolEventsOnDay(day).length > 0;
              const hasCustody = custodyEventsOnDay(day).length > 0;
              return (
                <div
                  key={day.toISOString()}
                  className={`relative flex h-9 flex-col items-center justify-center rounded-lg text-xs font-medium transition-colors ${
                    isToday(day) ? 'bg-primary text-primary-foreground' : 'text-foreground'
                  } ${!isSameMonth(day, currentMonth) ? 'text-muted-foreground/40' : ''}`}
                >
                  {day.getDate()}
                  {(hasEvents || hasCustody) ? (
                    <span className="absolute bottom-0.5 left-1/2 flex -translate-x-1/2 items-center gap-0.5">
                      {hasEvents ? (
                        <span className={`h-1 w-1 rounded-full ${isToday(day) ? 'bg-primary-foreground' : 'bg-accent'}`} />
                      ) : null}
                      {hasCustody ? (
                        <span className={`h-1 w-1 rounded-full ${isToday(day) ? 'bg-primary-foreground/70' : 'bg-teal'}`} />
                      ) : null}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </AppCard>

        <AppCard>
          <AppSectionHeader
            title="Next 14 days"
            subtitle="The next visible family dates across manual events and imported school dates."
            action={(
              <AppButton size="sm" onPress={openAdd}>
                <Plus size={14} /> Add event
              </AppButton>
            )}
          />
          {upcomingCalendarItems.length === 0 ? (
            <AppEmptyState
              title="No upcoming events yet"
              body="Add the next school, care, or family date so the shared Family shell stays useful."
            />
          ) : (
            <div className="space-y-2">
              {upcomingCalendarItems.map((entry) => (
                entry.source === 'manual' ? (
                  <AppPanelButton
                    key={entry.event.id}
                    type="button"
                    onClick={() => openEdit(entry.event)}
                    className={`gap-3 rounded-lg px-3 py-3 ${typeColors[entry.event.type]}`}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {entry.event.recurrence_type === 'custom' && '🔁 '}
                        {entry.event.title}
                      </p>
                      <p className="text-xs opacity-70">{format(new Date(entry.event.date), 'EEE d MMM')} · {entry.event.time}</p>
                    </div>
                  </AppPanelButton>
                ) : (
                  <div
                    key={entry.event.id}
                    className="w-full rounded-lg border border-border bg-plum-light p-3"
                  >
                    <p className="text-sm font-medium text-primary">{entry.event.title}</p>
                    <p className="text-xs text-primary/80 mt-1">
                      {format(new Date(entry.event.date), 'EEE d MMM')} · Imported school calendar
                    </p>
                    {entry.event.notes ? (
                      <p className="text-xs text-primary/80 mt-2 leading-relaxed">{entry.event.notes}</p>
                    ) : null}
                  </div>
                )
              ))}
            </div>
          )}
        </AppCard>
      </FamilyLifeSection>

      <FamilyLifeSection
        emoji={FAMILY_LIFE_SECTION_EMOJI.childcare}
        title="Childcare & shared care"
        subtitle="Childcare trackers, shared care, and care reviews in one place."
        open={openSections.childcare}
        onToggle={() => toggleSection('childcare')}
      >
        <FamilyChildcareSection />
        <FamilyCustodySection
          familyChildren={data.profile.children}
          custodyShare={custodyShare}
          custodyEvents={custodyEvents}
          custodyRequests={custodyRequests}
        />
        <FamilyCareReviewSection />
      </FamilyLifeSection>

      <FamilyAdminTab
        initialOpenSection={searchParams.get('tab') === 'admin' ? 'family-admin' : undefined}
        launchFamilyAdminItem={searchParams.get('compose') === 'family-admin'}
        onLaunchHandled={clearFamilyShortcut}
      />
    </div>
  );

  const tabs = [
    {
      key: 'calendar',
      label: 'Family life',
      subtitle: 'Profile, school dates, events, childcare, deadlines, and admin',
      content: calendarTab,
    },
  ];

  return (
    <>
      <TabbedPageShell
        title="My People"
        subtitle="Everyone in my life, everything coming up"
        tabs={tabs}
        nudge={<SectionNudge section="family" />}
        hero={(
          <FamilyJourneyCard
            onAddEvent={openAdd}
            onOpenFamilyLife={() => navigate('/family?tab=calendar')}
            onOpenHomeJobs={() => navigate('/home?tab=my-home')}
            action={(
              <SectionShortcutsLauncher
                title="Family shortcuts"
                subtitle="Start the next family admin job without opening another full panel."
                items={[
                  {
                    id: 'event',
                    label: 'Add family event',
                    description: 'Save the next school, care, or family date quickly.',
                    onPress: openAdd,
                  },
                  {
                    id: 'admin',
                    label: 'Add family admin item',
                    description: 'Capture the next school, childcare, or co-parenting task.',
                    onPress: openFamilyAdminShortcut,
                  },
                ]}
              />
            )}
          />
        )}
        guideRail={<SectionGuideRail section="family" />}
      />

      <Dialog open={eventOpen} onOpenChange={(open) => { if (!open) resetForm(); setEventOpen(open); }}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit event' : 'Add family event'}</DialogTitle>
            <DialogDescription>
              Keep the next school, care, or family date visible with the details you need in one place.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <AppFieldGroup label="Event title">
              <AppInput placeholder="Event title" value={title} onChange={(event) => setTitle(event.target.value)} />
            </AppFieldGroup>
            <AppDateField label="Date" value={date} onChange={setDate} />
            <AppFieldGroup label="Time">
              <AppInput type="time" value={time} onChange={(event) => setTime(event.target.value)} />
            </AppFieldGroup>
            <AppFieldGroup label="Type">
              <Select value={type} onValueChange={(value) => setType(value as CalendarEvent['type'])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((eventType) => (
                    <SelectItem key={eventType} value={eventType} className="capitalize">{eventType}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </AppFieldGroup>

            <AppFieldGroup label="Repeat">
              <RecurrenceBuilder value={recurrence} onChange={setRecurrence} />
            </AppFieldGroup>

            <AppButton onPress={handleSaveEvent}>
              {editingEvent ? 'Save changes' : 'Save'}
            </AppButton>

            {editingEvent && (
              <AppButton onPress={handleDeleteEvent} tone="destructive">
                <Trash2 size={14} /> Delete event
              </AppButton>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
